import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import type { AgentArtifact, AgentEmailDraft } from '../../types/agent';
import { ARTIFACT_ICON, ARTIFACT_LABEL, artifactRoute, btn, card } from './agentUi';
import EmailDraftEditor from './EmailDraftEditor';
import { openAuthedFileInNewTab, apiErrorMessage } from '../../services/agentApi';

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
const ORDER = ['report', 'certificate', 'acord_form', 'filled_template', 'email_draft', 'pdf', 'writeup', 'document', 'folder', 'customer', 'workflow_job'];

interface Props {
  artifacts: AgentArtifact[];
  drafts: AgentEmailDraft[];
  canEdit: boolean;
  onDraftChanged: (draft: AgentEmailDraft) => void;
  highlightId?: string | null;
}

const ArtifactPanel: React.FC<Props> = ({ artifacts, drafts, canEdit, onDraftChanged, highlightId }) => {
  const navigate = useNavigate();
  const [openId, setOpenId] = useState<string | null>(highlightId || null);
  const [error, setError] = useState<string | null>(null);

  const openFile = async (a: AgentArtifact) => {
    setError(null);
    try {
      if (a.fileId) {
        await openAuthedFileInNewTab(`${API_BASE_URL}/api/assistant/files/${a.fileId}`);
        return;
      }
      // Certificates/ACORD forms: open the flattened (final) copy when there is one.
      const documentId = (a.metadata?.flattenedDocumentId as string | undefined) || (a.refTable === 'policy_documents' ? a.refId : null);
      if (documentId) {
        await openAuthedFileInNewTab(`${API_BASE_URL}/api/documents/${documentId}/stream`);
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not open the file'));
    }
  };

  const grouped = ORDER.map((type) => ({ type, items: artifacts.filter((a) => a.type === type) })).filter((g) => g.items.length > 0);

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Artifacts</h3>
      {grouped.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">Nothing produced yet.</p>}
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      {grouped.map((group) => (
        <div key={group.type}>
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
            {ARTIFACT_ICON[group.type]} {ARTIFACT_LABEL[group.type] || group.type}{group.items.length > 1 ? `s (${group.items.length})` : ''}
          </div>
          <ul className="space-y-1.5">
            {group.items.map((a) => {
              const route = artifactRoute(a);
              const draft = a.type === 'email_draft' ? drafts.find((d) => d.id === a.refId) : undefined;
              const expanded = openId === a.id;
              return (
                <li key={a.id} className={`${card} p-2.5 ${highlightId === a.id ? 'ring-2 ring-primary/50' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm text-gray-900 dark:text-gray-100 break-words">{a.title}</div>
                      {a.status !== 'ready' && <div className={`text-xs ${a.status === 'failed' ? 'text-red-600' : 'text-amber-600'}`}>{a.status === 'pending' ? 'still running' : 'failed'}</div>}
                      {Array.isArray(a.metadata?.unresolvedKeys) && a.metadata.unresolvedKeys.length > 0 && (
                        <div className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">{a.metadata.unresolvedKeys.length} field(s) had no data on file</div>
                      )}
                      {draft && <div className="text-xs text-gray-500">To {draft.toAddresses.join(', ')} · {draft.status === 'draft' ? 'not sent' : draft.status}</div>}
                    </div>
                    <div className="flex flex-shrink-0 gap-1.5">
                      {(a.type === 'email_draft' || a.type === 'writeup') && (
                        <button className={btn.secondary} onClick={() => setOpenId(expanded ? null : a.id)}>{expanded ? 'Close' : a.type === 'email_draft' ? 'Edit' : 'Read'}</button>
                      )}
                      {(a.fileId || (a.refTable === 'policy_documents' && a.refId)) && (
                        <button className={btn.secondary} onClick={() => openFile(a)}>Open</button>
                      )}
                      {route && (
                        <button className={btn.secondary} onClick={() => navigate(route)}>{a.type === 'report' ? 'View' : 'Go'}</button>
                      )}
                    </div>
                  </div>
                  {expanded && draft && (
                    <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
                      <EmailDraftEditor draft={draft} canEdit={canEdit} onChanged={onDraftChanged} />
                    </div>
                  )}
                  {expanded && a.type === 'writeup' && (
                    <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3 text-sm text-gray-800 dark:text-gray-100 max-h-96 overflow-auto">
                      <ReactMarkdown>{String(a.metadata?.content || '')}</ReactMarkdown>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default ArtifactPanel;
